import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Users, Award, Globe } from 'lucide-react';
import { mockData } from '../mock';
import HomeSearch from '../components/HomeSearch';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1665519448106-e6ab874553aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w2ODU4NDd8MHwxfHNlYXJjaHwxfHxjYXIlMjBkZXRhaWxpbmd8ZW58MHx8fHwxNzU4MjE5MTQ2fDA&ixlib=rb-4.1.0&q=85" 
            alt="Premium luxury car detailing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-white/85"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h1 className="text-display-large text-gray-900 leading-tight">
                Premium
                <span className="block text-blue-500">
                  Automotive Excellence
                </span>
              </h1>
              <p className="text-body-large text-gray-600 max-w-3xl mx-auto">
                India's leading provider of premium automotive detailing services, professional training, 
                and specialized paint protection films designed for Indian conditions.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/services"
                className="bg-teal-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center space-x-2"
              >
                <span>Explore Services</span>
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/training#booking"
                className="border-2 border-teal-600 text-teal-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-teal-600 hover:text-white transition-all duration-200 flex items-center space-x-2"
              >
                <span>Book Training</span>
                <Users size={20} />
              </Link>
            </div>

            {/* Search Component */}
            <HomeSearch />

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-teal-600 mb-2">25+</div>
                <div className="text-gray-600 text-sm">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-teal-600 mb-2">100+</div>
                <div className="text-gray-600 text-sm">Trained Professionals</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-teal-600 mb-2">1</div>
                <div className="text-gray-600 text-sm">Training Center</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-teal-600 mb-2">10,000+</div>
                <div className="text-gray-600 text-sm">Satisfied Customers</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="space-y-4">
                <h2 className="text-section text-gray-900">
                  Meet the
                  <span className="text-teal-600"> Expert</span>
                </h2>
                <p className="text-body-large text-gray-600">
                  {mockData.founder.bio}
                </p>
              </div>
              
              <div className="space-y-4">
                {mockData.founder.specializations.map((spec, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Star className="text-teal-600" size={20} />
                    <span className="text-gray-700">{spec}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/about"
                className="inline-flex items-center space-x-2 text-teal-600 hover:text-teal-700 font-semibold transition-colors"
              >
                <span>Learn More About Nirmal</span>
                <ArrowRight size={18} />
              </Link>
            </div>

            <div className="relative">
              <div className="bg-slate-50 rounded-2xl p-8 border border-gray-200">
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <Award className="text-teal-600" size={24} />
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{mockData.founder.name}</h3>
                      <p className="text-gray-600">{mockData.founder.title}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-teal-600">{mockData.founder.experience}</div>
                      <div className="text-gray-500 text-sm">Experience</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-teal-600">{mockData.founder.studentsTrained}</div>
                      <div className="text-gray-500 text-sm">Students Trained</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-gray-900">
              Our
              <span className="text-teal-600"> Services</span>
            </h2>
            <p className="text-body-large text-gray-600 max-w-3xl mx-auto">
              Comprehensive automotive solutions for premium vehicles and professional development
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.services.slice(0, 6).map((service) => (
              <div key={service.id} className="bg-white rounded-xl p-6 border border-gray-200 hover:border-teal-300 transition-all duration-300 hover:-translate-y-2 hover:shadow-lg">
                <div className="space-y-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold">
                      {service.icon === 'Shield' && '🛡️'}
                      {service.icon === 'Sparkles' && '✨'}
                      {service.icon === 'Zap' && '⚡'}
                      {service.icon === 'Car' && '🚗'}
                      {service.icon === 'Wrench' && '🔧'}
                      {service.icon === 'Settings' && '⚙️'}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.slice(0, 2).map((feature, index) => (
                      <li key={index} className="text-gray-500 text-sm flex items-center space-x-2">
                        <Star className="text-teal-600" size={12} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 space-y-4">
            <Link
              to="/services"
              className="inline-flex items-center space-x-2 bg-teal-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <span>View All Services</span>
              <ArrowRight size={20} />
            </Link>
            <div className="text-center">
              <Link
                to="/e-warranty"
                className="inline-flex items-center space-x-2 text-teal-600 hover:text-teal-700 font-semibold transition-colors"
              >
                <span>Register Your Service for eWarranty Protection</span>
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-50 border-y border-teal-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-section text-gray-900">
            Ready to Experience
            <span className="text-teal-600"> Premium Quality?</span>
          </h2>
          <p className="text-body-large text-gray-600">
            Join thousands of satisfied customers and certified professionals who trust Unisol Global 
            for their automotive needs and career development.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-teal-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Get Your Quote Today
            </Link>
            <Link
              to="/training#booking"
              className="border-2 border-teal-600 text-teal-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-teal-600 hover:text-white transition-all duration-200"
            >
              Book Training Session
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;